import { ApolloLink, Operation } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { store } from './storage';
import { createStore } from '../stores/create-store';
import { hasGraphQLError } from '../helpers/tracked-query.helpers';
import { getQueueOpenValue } from '../stores/connectivity.store';

const STORAGE_REF = 'max_TrackedQueries';
let currentTrackedQueries: ITrackedQuery[] = [];

const [, getTrackedQueries, setTrackedQueries] = createStore<ITrackedQuery[]>([]);
export { getTrackedQueries, setTrackedQueries };

export const removeTrackedQuery = async (id: string) => {
  const nextTrackedQueries = getTrackedQueries().filter(query => query.id !== id);

  setTrackedQueries(nextTrackedQueries);
  await store.set(STORAGE_REF, JSON.stringify({ queries: nextTrackedQueries }));
};

export const addTrackedQuery = async (query: ITrackedQuery) => {
  const nextTrackedQueries = [...getTrackedQueries(), query];

  setTrackedQueries([...nextTrackedQueries]);
  await store.set(STORAGE_REF, JSON.stringify({ queries: nextTrackedQueries }));
};

export const createTrackerLink = () =>
  new ApolloLink((operation: Operation, forward) => {
    if (forward === undefined) {
      return null;
    }

    // Read on entry: a send through an open queue answers the user live; a closed queue holds it.
    const sentLive = getQueueOpenValue();

    const name: string = operation.operationName;
    const queryJSON: string = JSON.stringify(operation.query);
    const variablesJSON: string = JSON.stringify(operation.variables);
    const context = operation.getContext();
    const contextJSON = JSON.stringify({}); // JSON.stringify(context);
    const id = uuidv4();

    if (context.tracked !== undefined) {
      const trackedQuery: ITrackedQuery = {
        contextJSON,
        id,
        name,
        queryJSON,
        variablesJSON,
      };
      let addQuery = true;

      try {
        if (name === 'RemoveScore' && operation.variables?._id.startsWith('src_')) {

          const currQueries = getTrackedQueries();

          let found = false;
          for (let i = 0; i < currQueries.length && !found; i +=1) {
            const query = currQueries[i];
            const currVariables = JSON.parse(query.variablesJSON);

            if (currVariables.data.drillId == operation.variables.data.drillId
              && currVariables.data.skillId == operation.variables.data.skillId
              && currVariables.data.assessmentSessionId == operation.variables.data.assessmentSessionId
              && currVariables.data.playerAssessmentId == operation.variables.data.playerAssessmentId
              && currVariables.data.round == operation.variables.data.round) {
              removeTrackedQuery(query.id);
              found = true;
              addQuery = false;
            }
          }
        } else if (operation.variables?.data?._id && operation.variables.data._id.startsWith('src_')) {
          const currQueries = getTrackedQueries();

          let found = false;
          for (let i = 0; i < currQueries.length && !found; i +=1) {
            const query = currQueries[i];
            const currVariables = JSON.parse(query.variablesJSON);

            if (currVariables.data.drillId == operation.variables.data.drillId
              && currVariables.data.skillId == operation.variables.data.skillId
              && currVariables.data.assessmentSessionId == operation.variables.data.assessmentSessionId
              && currVariables.data.playerAssessmentId == operation.variables.data.playerAssessmentId
              && currVariables.data.round == operation.variables.data.round) {
              removeTrackedQuery(query.id);
              delete operation.variables.data._id;
              trackedQuery.variablesJSON = JSON.stringify(operation.variables);
              found = true;
            }
          }
        }

        if (addQuery) {
          addTrackedQuery(trackedQuery);
        }
      } catch (err) {
        console.error('Should not be breaking here...', err);
      }
    }

    return forward(operation).map(data => {
      // A live send's error reached the user, who may already have corrected it, so it is never
      // replayed. See "Tracked mutations replay only what the user never saw answered" in CLAUDE.md.
      if (context.tracked !== undefined && (sentLive || !hasGraphQLError(data))) {
        removeTrackedQuery(id);
      }

      return data;
    });
  });

export const checkHasTrackedQueries = async () => {
  try {
    currentTrackedQueries = JSON.parse(await store.get(STORAGE_REF) || '').queries;
  } catch(e) {
    currentTrackedQueries = [];
  }

  return currentTrackedQueries.length > 0;
}
